import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  Play,
  Image as ImageIcon,
  Edit3,
  Trash2,
  Clock,
  Tag,
  FileText,
  Copy,
  Download,
  AlertCircle
} from 'lucide-react';
import { Card, Badge } from '../../styles/styled-components';
import { MediaPreview } from '../../types/api';

const MediaCardContainer = styled(Card)`
  overflow: hidden;
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[4]};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const MediaPreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 180px;
    margin-bottom: ${({ theme }) => theme.spacing[3]};
  }
`;

const MediaPreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MediaPreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MediaPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const MediaOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.primary}20 0%, transparent 50%, ${theme.colors.primary}10 100%)`};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  ${MediaPreviewContainer}:hover & {
    opacity: 1;
  }
`;

const PlayButton = styled(motion.button)`
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.lg};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const MediaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const MediaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const MediaTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  flex: 1;
  word-break: break-word;
`;

const MediaActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const MediaMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MediaDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const KeywordContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const KeywordBadge = styled(Badge)`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const MediaTypeIndicator = styled.div<{ $tipo: 'video' | 'image' }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing[2]};
  left: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  backdrop-filter: blur(8px);
`;

interface MediaCardProps {
  media: MediaPreview;
  onEdit?: (media: MediaPreview) => void;
  onDelete?: (media: MediaPreview) => void;
  onPreview?: (media: MediaPreview) => void;
}

const MediaCard: React.FC<MediaCardProps> = ({
  media,
  onEdit,
  onDelete,
  onPreview
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    setImageError(false);

    // A URL já vem completa do backend ou do hook useMidias
    if (media.url) {
      console.log('[MediaCard] URL da mídia:', media.url);
      setImageUrl(media.url);
    } else {
      setImageUrl('');
    }
  }, [media.fileName, media.url, media.exists]);

  const handleImageError = (e: any) => {
    console.error('[MediaCard] Erro ao carregar imagem:', {
      fileName: media.fileName,
      url: imageUrl,
      tipo: media.tipo,
      error: e
    });
    setImageError(true);
  };

  const copyFileName = () => {
    navigator.clipboard.writeText(media.fileName);
  };

  const downloadFile = () => {
    if (!media.url) {
      console.warn('[MediaCard] Download indisponível para', media.fileName);
      return;
    }

    const link = document.createElement('a');
    link.href = media.url;
    link.download = media.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MediaCardContainer
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      hover
    >
      <MediaPreviewContainer>
        <MediaTypeIndicator $tipo={media.tipo}>
          {media.tipo === 'video' ? <Play size={12} /> : <ImageIcon size={12} />}
          {media.tipo === 'video' ? 'Vídeo' : 'Imagem'}
        </MediaTypeIndicator>

        {imageError ? (
          <MediaPlaceholder>
            <AlertCircle size={32} color="#ef4444" />
            <span style={{ fontSize: '11px', marginTop: '8px', maxWidth: '200px', wordBreak: 'break-all' }}>
              Erro ao carregar<br/>
              {imageUrl && <small title={imageUrl}>{imageUrl.substring(0, 50)}...</small>}
            </span>
          </MediaPlaceholder>
        ) : imageUrl ? (
          media.tipo === 'video' ? (
            <MediaPreviewVideo
              src={imageUrl}
              onError={handleImageError}
            />
          ) : (
            <MediaPreviewImage
              src={imageUrl}
              alt={media.fileName}
              onError={handleImageError}
            />
          )
        ) : (
          <MediaPlaceholder>
            {media.tipo === 'video' ? <Play size={32} /> : <ImageIcon size={32} />}
            <span>Sem preview</span>
          </MediaPlaceholder>
        )}

        <MediaOverlay>
          <PlayButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => media.url && onPreview?.(media)}
            disabled={!media.url || imageError}
          >
            {media.tipo === 'video' ? <Play size={24} /> : <ImageIcon size={24} />}
          </PlayButton>
        </MediaOverlay>
      </MediaPreviewContainer>

      <MediaInfo>
        <MediaHeader>
          <MediaTitle>{media.fileName}</MediaTitle>
          <MediaActions>
            <ActionButton onClick={copyFileName} title="Copiar nome">
              <Copy size={16} />
            </ActionButton>
            <ActionButton onClick={downloadFile} title="Download">
              <Download size={16} />
            </ActionButton>
            <ActionButton onClick={() => onEdit?.(media)} title="Editar">
              <Edit3 size={16} />
            </ActionButton>
            <ActionButton onClick={() => onDelete?.(media)} title="Excluir">
              <Trash2 size={16} />
            </ActionButton>
          </MediaActions>
        </MediaHeader>

        <MediaMeta>
          <MetaItem>
            <Clock size={14} />
            <span>{media.delaySegundos}s</span>
          </MetaItem>
          <MetaItem>
            <Tag size={14} />
            <span>{media.palavrasChave.length} palavra{media.palavrasChave.length !== 1 ? 's' : ''}</span>
          </MetaItem>
          {media.descricao && (
            <MetaItem>
              <FileText size={14} />
              <span>Com descrição</span>
            </MetaItem>
          )}
        </MediaMeta>

        {media.descricao && (
          <MediaDescription>{media.descricao}</MediaDescription>
        )}

        <KeywordContainer>
          {media.palavrasChave.slice(0, 4).map((keyword, index) => (
            <KeywordBadge key={index} variant="primary" size="sm">
              {keyword}
            </KeywordBadge>
          ))}
          {media.palavrasChave.length > 4 && (
            <KeywordBadge variant="primary" size="sm">
              +{media.palavrasChave.length - 4}
            </KeywordBadge>
          )}
        </KeywordContainer>
      </MediaInfo>
    </MediaCardContainer>
  );
};

export default MediaCard;